/**
 * Reusable destructive-action confirmation dialog built on the native `<dialog>` element.
 *
 * Caller controls visibility via a forwarded ref:
 *   `dialogRef.current?.showModal()` — open
 *   `dialogRef.current?.close()`      — close
 *
 * Pattern matches the shell's existing inline dialog usage (LocationHolidayListPage) but
 * extracted as a shared, branded component so every feature gets consistent UX without
 * duplicating markup, styles, or loading/error logic.
 */
import { Button } from "@coordio/ui";
import { AlertTriangle, Loader2, Trash2 } from "lucide-react";
import {
  forwardRef,
  type ReactNode,
  useCallback,
  useImperativeHandle,
  useLayoutEffect,
  useRef,
  useState,
} from "react";

import "./DeleteDialog.scss";

// ─── Public API ──────────────────────────────────────────────────────────────

export type DeleteDialogProps = {
  /** Dialog heading. Defaults to "Delete record?" */
  title?: string;
  /**
   * One-line description of the item being deleted (name, identifier, etc.).
   * Displayed in a highlighted callout so the user can confirm the right record.
   */
  entityLabel?: ReactNode;
  /**
   * Additional explanatory text shown below the entity label.
   * Use to communicate cascading effects or irreversibility.
   */
  description?: ReactNode;
  /** Async callback — the dialog manages its own busy state while this resolves. */
  onConfirm: () => Promise<void>;
  /** Called when the user clicks Cancel or presses Escape. */
  onCancel: () => void;
  /** Custom label for the confirm button. Defaults to "Delete". */
  confirmLabel?: string;
  /** Button variant for the confirm action. Defaults to "destructive". */
  confirmVariant?: "default" | "destructive";
  /** Loading label shown while the async confirm action is in flight. */
  busyLabel?: string;
  /** Optional icon override for non-delete confirmations like activate/deactivate. */
  dialogIcon?: ReactNode;
};

/**
 * Imperative handle exposed via `ref`.  Callers open/close the dialog with
 * the same native API used by the rest of the shell.
 */
export type DeleteDialogHandle = {
  showModal: () => void;
  close: () => void;
};

// ─── Component ───────────────────────────────────────────────────────────────

/**
 * Branded confirmation dialog for permanent-delete actions.
 *
 * @example
 * ```tsx
 * const dialogRef = useRef<DeleteDialogHandle>(null);
 *
 * <Button onClick={() => dialogRef.current?.showModal()}>Delete</Button>
 *
 * <DeleteDialog
 *   ref={dialogRef}
 *   title="Delete Location Holiday?"
 *   entityLabel={`${row.locationName} — ${row.holidayName}`}
 *   onConfirm={async () => { await service.deleteById(row.id); }}
 *   onCancel={() => dialogRef.current?.close()}
 * />
 * ```
 */
export const DeleteDialog = forwardRef<DeleteDialogHandle, DeleteDialogProps>(
  function DeleteDialog(
    {
      title = "Delete record?",
      entityLabel,
      description,
      onConfirm,
      onCancel,
      confirmLabel = "Delete",
      confirmVariant = "destructive",
      busyLabel = "Deleting…",
      dialogIcon,
    },
    ref,
  ) {
    const dialogRef = useRef<HTMLDialogElement>(null);
    /** WHY: List pages pass inline `onConfirm` that closes over `pendingItem`; table action buttons may
     *      retain a stale `onClick`. Always dispatch the latest callback without changing handler identity.
     */
    const onConfirmRef = useRef(onConfirm);
    const onCancelRef = useRef(onCancel);
    useLayoutEffect(() => {
      onConfirmRef.current = onConfirm;
      onCancelRef.current = onCancel;
    });
    /** WHY: `setIsDeleting(true)` is async-by-render; `.close()` runs `onClose` in the same turn, which
     *      must not treat programmatic post-success close like dismiss — ref updates synchronously. */
    const confirmBusyRef = useRef(false);
    const [isDeleting, setIsDeleting] = useState(false);
    const [error, setError] = useState<string | null>(null);

    useImperativeHandle(ref, () => ({
      showModal() {
        setError(null);
        dialogRef.current?.showModal();
      },
      close() {
        dialogRef.current?.close();
      },
    }));

    const handleCancel = useCallback(() => {
      if (confirmBusyRef.current) return;
      setError(null);
      onCancelRef.current();
    }, []);

    const handleConfirm = useCallback(async () => {
      setError(null);
      confirmBusyRef.current = true;
      setIsDeleting(true);
      let success = false;
      try {
        await onConfirmRef.current();
        success = true;
        dialogRef.current?.close();
        setError(null);
      } catch (e) {
        setError(e instanceof Error ? e.message : "An error occurred. Please try again.");
      } finally {
        confirmBusyRef.current = false;
        setIsDeleting(false);
        // WHY: Parent `onCancel` clears staged row state (`pendingItem` → null). If that runs in the same
        //      turn as `close()`, ConfirmDialog props fall back to the delete variant briefly while the
        //      native `<dialog>` is still paints-closing → visible flicker ("Delete …?" flash).
        if (success) {
          queueMicrotask(() => {
            onCancelRef.current();
          });
        }
      }
    }, []);

    const handleDialogClose = useCallback(() => {
      if (confirmBusyRef.current) return;
      setError(null);
      onCancelRef.current();
    }, []);

    const handleBackdropClick = useCallback(
      (e: React.MouseEvent<HTMLDialogElement>) => {
        if (confirmBusyRef.current) return;
        if (e.target === dialogRef.current) {
          handleCancel();
        }
      },
      [handleCancel],
    );

    return (
      <dialog
        ref={dialogRef}
        className="delete-dialog"
        aria-labelledby="delete-dialog-title"
        aria-describedby="delete-dialog-body"
        onClose={handleDialogClose}
        onClick={handleBackdropClick}
        data-testid="delete-dialog"
      >
        <div className="delete-dialog__panel" onClick={(e) => e.stopPropagation()}>
          {/* ── Icon header ─────────────────────────────────────────────── */}
          <div className="delete-dialog__icon-wrap" aria-hidden>
            {dialogIcon ?? <Trash2 className="delete-dialog__icon" />}
          </div>

          {/* ── Title ───────────────────────────────────────────────────── */}
          <h2 id="delete-dialog-title" className="delete-dialog__title">
            {title}
          </h2>

          {/* ── Body ────────────────────────────────────────────────────── */}
          <div id="delete-dialog-body" className="delete-dialog__body">
            {entityLabel && (
              <div className="delete-dialog__entity" data-testid="delete-dialog-entity">
                {entityLabel}
              </div>
            )}

            {description ? (
              <p className="delete-dialog__description">{description}</p>
            ) : (
              <p className="delete-dialog__description">
                This action is <strong>permanent</strong> and cannot be undone.
              </p>
            )}
          </div>

          {/* ── Error ───────────────────────────────────────────────────── */}
          {error && (
            <div className="delete-dialog__error" role="alert" data-testid="delete-dialog-error">
              <AlertTriangle className="delete-dialog__error-icon" aria-hidden />
              <span>{error}</span>
            </div>
          )}

          {/* ── Actions ─────────────────────────────────────────────────── */}
          <div className="delete-dialog__actions">
            <Button
              type="button"
              variant="outline"
              disabled={isDeleting}
              onClick={handleCancel}
              data-testid="delete-dialog-cancel"
            >
              Cancel
            </Button>
            <Button
              type="button"
              variant={confirmVariant}
              disabled={isDeleting}
              onClick={() => void handleConfirm()}
              data-testid="delete-dialog-confirm"
              aria-busy={isDeleting}
            >
              {isDeleting ? (
                <>
                  <Loader2 className="delete-dialog__spinner" aria-hidden />
                  {busyLabel}
                </>
              ) : (
                confirmLabel
              )}
            </Button>
          </div>
        </div>
      </dialog>
    );
  },
);
